import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {
  MachineCategoryBaseColumnTypes
} from '@constants/administration/portal-setup/machine-category/machine-category-base-column-types';
import {
  MachineCategoryDefaultColumnTypes
} from '@constants/administration/portal-setup/machine-category/machine-category-default-column-types';
import {CategoryColumn} from '@models/adminstration/portal-setup/machine-category/category-column';
import {Machine} from '@models/machine/machine';
import {ValidatorsService} from '@services/validators/validators.service';
import {takeUntil} from 'rxjs/operators';
import {ReplaySubject} from 'rxjs';
import {MetaData} from '@models/adminstration/meta-data';

@Component({
    selector: 'scitis-machine-category-edit-column',
    templateUrl: './machine-category-edit-column.component.html',
    styleUrls: ['./machine-category-edit-column.component.scss'],
    standalone: false
})
export class MachineCategoryEditColumnComponent implements OnInit, OnDestroy {

  @Input()
  public baseColumn = new CategoryColumn();

  @Input()
  public refMachine: Machine = new Machine();

  @Input()
  public metaDataForRefMachine: MetaData[] = [];

  @Input()
  public signalsForRefMachine: any = {};

  @Input()
  public isLoadingMachine: boolean = false;

  @Output()
  isColumnFormInvalid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  last: boolean = false;

  public columnForm: UntypedFormGroup = new UntypedFormGroup({});
  public baseColumnTypes: string[] = Object.values(MachineCategoryBaseColumnTypes);
  public defaultColumnTypes = MachineCategoryDefaultColumnTypes;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private validators: ValidatorsService
  ) { }

  ngOnInit(): void {
    this.createColumnForm();
    this.onDefaultColumnTypeChange(this.baseColumn.name);
  }

  get f() {
    return this.columnForm.controls;
  }

  isDefaultType(): boolean {
    return this.f.baseColumnType.value === MachineCategoryBaseColumnTypes.DEFAULT;
  }

  isMetadataType() {
    return this.f.baseColumnType.value === MachineCategoryBaseColumnTypes.METADATA;
  }

  isSignalType() {
    return this.f.baseColumnType.value === MachineCategoryBaseColumnTypes.SIGNAL;
  }

  onColumnTypeChange() {
    this.baseColumn.type = this.f.baseColumnType.value;
    if (!this.isSignalType()) {
      this.f.sortable.setValue(false, {emitEvent: false});
      this.baseColumn.sortable = false;
    }
  }

  onDefaultColumnTypeChange(value) {
    this.baseColumn.name = value;
    for (const [key, value] of Object.entries(this.defaultColumnTypes)) {
      if (this.baseColumn.name === key) {
        this.baseColumn.label = value.label;
      }
    }
    this.isColumnFormInvalid.emit(this.columnForm.invalid);
  }

  onMetadataColumnTypeChange(value) {
    this.baseColumn.name = value?.key || '';
    this.baseColumn.label = value?.key || value?.name || '';
    this.isColumnFormInvalid.emit(this.columnForm.invalid);
  }

  onSignalColumnTypeChange(value) {
    const signal = this.signalsForRefMachine[value];
    this.baseColumn.name = signal?.name || '';
    this.baseColumn.label = signal?.label || signal?.name || '';
    this.isColumnFormInvalid.emit(this.columnForm.invalid);
  }

  onSignalSortableChange() {
    this.baseColumn.sortable = this.isSignalType() && this.f.sortable.value === true;
  }

  private createColumnForm() {
    this.columnForm = new UntypedFormGroup({
      baseColumnType: new UntypedFormControl(
        {value: this.baseColumn.type, disabled: !this.baseColumn.deletable},
        [Validators.required], [this.validators.duplicatedColumnsValidator()]
      ),
      defaultColumnType: new UntypedFormControl({value: this.baseColumn.name, disabled: !this.baseColumn.deletable}),
      metadataColumnType: new UntypedFormControl(this.baseColumn.name),
      signalColumnType: new UntypedFormControl(this.baseColumn.name),
      sortable: new UntypedFormControl(this.baseColumn.sortable === true),
    });
    if (this.baseColumn.deletable) {
      this.setAndClearDynamicValidators(this.baseColumn.type as MachineCategoryBaseColumnTypes, false);
    }
    this.columnForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.isColumnFormInvalid.emit(this.columnForm.invalid));
    this.f.baseColumnType.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.setAndClearDynamicValidators(value);
    });
  }

  private setAndClearDynamicValidators(value: MachineCategoryBaseColumnTypes, withReset: boolean = true) {
      switch (value) {
        case MachineCategoryBaseColumnTypes.DEFAULT:
          this.chooseActiveField('defaultColumnType', ['metadataColumnType', 'signalColumnType'], withReset);
          break;
        case MachineCategoryBaseColumnTypes.METADATA:
          this.chooseActiveField('metadataColumnType', ['defaultColumnType', 'signalColumnType'], withReset);
          break;
        case MachineCategoryBaseColumnTypes.SIGNAL:
          this.chooseActiveField('signalColumnType', ['defaultColumnType', 'metadataColumnType'], withReset);
          break;
      }
  }

  private chooseActiveField(active: string, passive: string[], withReset: boolean = true): void {
    this.f[active].setValidators([Validators.required]);
    this.f[active].setAsyncValidators([this.validators.duplicatedColumnsValidator()]);
    if (withReset) {
      this.f[active].reset();
    }
    this.f[active].updateValueAndValidity();
    passive.forEach(name => {
      this.f[name].clearAsyncValidators();
      this.f[name].clearValidators();
      this.f[name].reset();
      this.f[name].updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    this.isColumnFormInvalid.emit(false);
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
